import * as React from 'react';
import { cn } from '@/lib/utils';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { getAvatarColor, getAvatarInitial } from '@/lib/avatar-color';
import { User } from '@/icons/Icon';

type Shape = 'circle' | 'square';
type Fallback = 'initial' | 'silhouette';

type Props = {
  /**
   * Source used for both the deterministic background colour and the rendered
   * initial. Callers that already hold a single derived initial may pass it
   * directly — `getAvatarInitial` of an upper-cased single char is a no-op,
   * so colour + glyph stay identical to passing the full name.
   */
  name: string;
  /**
   * Uploaded photo URL. Rendered over the fallback when set; Radix swaps back
   * to the fallback on its own when the image is absent or fails to load, so
   * a dead URL degrades to `fallback` rather than a broken-image glyph.
   */
  src?: string | null;
  /**
   * What renders when there is no usable `src`. `initial` (the default, and
   * what every pre-existing call site gets) is the deterministic tinted
   * letter; `silhouette` is the neutral brand-tinted person glyph used by
   * profile surfaces, where a letter reads as data the user never set.
   */
  fallback?: Fallback;
  /**
   * `square` swaps the circular fallback for the rounded-md tile used by the
   * workspace switcher. Defaults to `circle`.
   */
  shape?: Shape;
  /** Merged onto the `Avatar` root — drives the per-site dimensions. */
  className?: string;
  /** Merged onto the `AvatarFallback` — drives the per-site text size. */
  fallbackClassName?: string;
  style?: React.CSSProperties;
};

/**
 * Initial-with-tinted-background avatar shared by the activity feed, account
 * peek panel, sidebar account menu, and workspace switcher. The fallback
 * background is the deterministic `getAvatarColor(name)` and the glyph is
 * `getAvatarInitial(name)`; the warm `text-ink-800 font-semibold` ink is
 * shared across every site.
 */
export function UserAvatar({
  name,
  src,
  fallback = 'initial',
  shape = 'circle',
  className,
  fallbackClassName,
  style,
}: Props) {
  const silhouette = fallback === 'silhouette';
  return (
    <Avatar
      className={cn(shape === 'square' && 'rounded-md', className)}
      style={style}
    >
      {src ? <AvatarImage src={src} alt="" /> : null}
      <AvatarFallback
        className={cn(
          'font-semibold text-ink-800',
          shape === 'square' && 'rounded-md',
          fallbackClassName,
        )}
        style={
          silhouette
            ? { background: 'var(--brand-100)', color: 'var(--brand-600)' }
            : { background: getAvatarColor(name) }
        }
      >
        {silhouette ? (
          // Sized in % so the glyph tracks whatever dimensions `className`
          // sets (h-8 in the sidebar, larger elsewhere) — inline style beats
          // the width/height attributes `make()` puts on the svg.
          <User aria-hidden style={{ width: '58%', height: '58%' }} />
        ) : (
          getAvatarInitial(name)
        )}
      </AvatarFallback>
    </Avatar>
  );
}
